import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientLicenseEntity } from './client-license.entity';
import type { LicenseNoteEntity } from './license-note.entity';

/**
 * Which compliance list the review was recorded from. The first (and today
 * only) value is the Adverse Action list: the state board has a disciplinary
 * action on record against a license that is STILL IN FORCE, so the account
 * can keep ordering on it.
 *
 * Stored as a plain varchar (not a MySQL enum) so a second list — a dead
 * license review, a collision review — is an ADDITIVE change the custom
 * database sync can carry without an ALTER. Same convention as
 * `SensorFaultReason` (sensor-alert.entity.ts).
 */
export type LicenseComplianceReviewType = 'adverse_action';

/**
 * What the compliance officer decided. Also a plain varchar for the same
 * reason — the disposition vocabulary is a product decision that will grow.
 *
 *   no_concern   — "Reviewed — no concern". The board acted; we are content
 *                  to keep trading on the license as it stands.
 *   action_taken — "Reviewed — action taken". Something was done off-system
 *                  (account held, rep briefed, documents requested); the note
 *                  says what.
 *   follow_up    — "Follow up on <date>". Suppressed for now, with a date the
 *                  officer wants it back in front of them. The UI defaults
 *                  that date to the license's own expiration/renewal date;
 *                  the API stores whatever it is given.
 *   reopened     — "Re-opened" (PD-16615). Not a decision an officer picks in
 *                  the review form: it is the UNDO of the decision before it.
 *                  Written by `reopenReview` as a new row (the table stays
 *                  insert-only, so the undone decision stays in the history)
 *                  and it never binds, so the item returns to its list at
 *                  once. A decision recorded after it hides the item again.
 */
export type LicenseComplianceReviewDisposition =
	| 'no_concern'
	| 'action_taken'
	| 'follow_up'
	| 'reopened';

/**
 * The dispositions an officer can RECORD from the review form. `reopened` is
 * absent on purpose: it is only ever written by the re-open action.
 */
export type LicenseComplianceReviewDecision = Exclude<
	LicenseComplianceReviewDisposition,
	'reopened'
>;

/**
 * Display wording for each disposition — one table for the audit row, the
 * list rows and the review modal, so they cannot name a decision two ways.
 */
export const LICENSE_COMPLIANCE_REVIEW_DISPOSITION_LABELS: Record<
	LicenseComplianceReviewDisposition,
	string
> = {
	no_concern: 'Reviewed — no concern',
	action_taken: 'Reviewed — action taken',
	follow_up: 'Follow up',
	reopened: 'Re-opened',
};

/**
 * One recorded compliance-review decision against one license.
 *
 * WHY THIS TABLE EXISTS. A board record that a compliance officer has already
 * read and accepted should not keep asking to be read. But there is no
 * permanent dismiss: the suppression is bound to a SNAPSHOT of the board
 * record as it stood when the officer reviewed it, and goes inert by plain
 * comparison at read time when the board's story changes — at which point the
 * item re-raises with the NEW facts. That is the mechanism the owner set on
 * 2026-05-16 for discrepancy snoozes
 * (`license-discrepancy-acknowledgement.entity.ts`), reused rather than
 * reinvented.
 *
 * TWO DELIBERATE DIFFERENCES from the discrepancy precedent, both of which
 * were gaps in it:
 *
 *   1. A REASON IS MANDATORY. Every row is created together with a
 *      `license_note` row carrying the officer's note, in one transaction.
 *      `fk_license_note__id` points at it. The note lives on the LICENSE, not
 *      on the client — whether the sales team should see these is a decision
 *      the owner deferred, so nothing here writes to `client_note` or relays
 *      the note onto the client activity feed.
 *   2. THERE IS AN AUDIT TRAIL. Rows are INSERT-only; a re-review inserts a
 *      new row rather than updating the last one, so the sequence of
 *      decisions on a license is readable straight off this table. The
 *      current decision is the newest non-deleted row for
 *      (license, review_type). A `license_activity` row is written with each
 *      one (`compliance_review_recorded`).
 *
 * WHAT THE SNAPSHOT BINDS TO — see `license-compliance-review-fingerprint.ts`
 * in crm-api for the full reasoning. Three fields, and deliberately NOT the
 * `lm_registry.has_disciplinary_action` flag itself: that flag oscillates
 * daily on some rows because two writers fight over it (PD-16292), and a
 * review bound to it would re-raise every single day. The flag decides
 * MEMBERSHIP of the list, not what was reviewed; when it flips off the item
 * simply leaves the list, and when it flips back on this review still binds.
 */
@Index('idx_lcr_license_type', ['fkClientLicenseId', 'reviewType'], {})
@Index('idx_lcr_followup', ['reviewType', 'followUpDate'], {})
@Entity('client_license_compliance_review')
export class LicenseComplianceReviewEntity extends BaseEntity {
	@Column('int', { name: 'fk_client_license__id', unsigned: true })
	public fkClientLicenseId!: number;

	@Column('varchar', { name: 'review_type', length: 50 })
	public reviewType!: LicenseComplianceReviewType;

	@Column('varchar', { name: 'disposition', length: 50 })
	public disposition!: LicenseComplianceReviewDisposition;

	/**
	 * Only meaningful for `disposition = 'follow_up'`. A date, not a datetime:
	 * the officer picks a day, and the UI defaults it to the license's own
	 * expiration/renewal date.
	 */
	@Column('date', { name: 'follow_up_date', nullable: true })
	public followUpDate!: string | null;

	/**
	 * The `license_note` row created in the same transaction as this review.
	 * Nullable only so the FK can be SET NULL if the note is ever hard-deleted
	 * — the service refuses to record a review without a note.
	 */
	@Column('int', {
		name: 'fk_license_note__id',
		unsigned: true,
		nullable: true,
	})
	public fkLicenseNoteId!: number | null;

	// ── Snapshot: the board record AS REVIEWED ──────────────────────────────
	// These three columns are the whole re-raise mechanism. They are compared
	// by plain equality against the live board record at read time; any
	// difference makes this review inert and the item re-raises.

	/**
	 * `lm_registry.id` of the board record that was reviewed. Re-linking the
	 * license to a different registry row is a different board record, so the
	 * review stops binding.
	 */
	@Column('int', {
		name: 'reviewed_registry_license_id',
		unsigned: true,
		nullable: true,
	})
	public reviewedRegistryLicenseId!: number | null;

	/**
	 * `lm_registry.license_status_normalized` as reviewed — the canonical
	 * bucket (Active / Probation / Suspended …), not the board's raw dialect,
	 * so a source re-wording ("Clear" → "Current") does not re-raise but a
	 * genuine move from Active to Probation does.
	 */
	@Column('varchar', {
		name: 'reviewed_registry_status',
		length: 100,
		nullable: true,
	})
	public reviewedRegistryStatus!: string | null;

	/**
	 * SHA-256 of the CANONICALIZED `lm_registry.disciplinary_detail` — the
	 * board's own description of the action (type, case number, dates). NULL
	 * when the board published a flag with no describable detail, which is
	 * the majority of flagged rows fleet-wide.
	 *
	 * A hash rather than a copy: the detail is a JSON blob of unbounded size
	 * on some sources (WI publishes an array of orders), it is the board's
	 * text to re-read rather than the tenant's to hold, and an exact 64-char
	 * comparison is what the drift test needs.
	 */
	@Column('char', {
		name: 'reviewed_detail_fingerprint',
		length: 64,
		nullable: true,
	})
	public reviewedDetailFingerprint!: string | null;

	/**
	 * NON-BINDING forensics: what else the board record read at review time
	 * (the disciplinary flag, source system, last sync). Recorded so a later
	 * question of the form "did the flag oscillation cause this?" can be
	 * answered from the row. Nothing in here takes part in the drift test —
	 * adding a field to it can never change suppression behavior.
	 */
	@Column('json', { name: 'reviewed_context', nullable: true })
	public reviewedContext!: Record<string, unknown> | null;

	/** Tenant `user._id` of the officer who recorded the decision. */
	@Column('int', {
		name: 'reviewed_by_user_id',
		unsigned: true,
		nullable: true,
	})
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewed_at' })
	public reviewedAt!: Date;

	@ManyToOne(() => ClientLicenseEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client_license__id', referencedColumnName: 'id' }])
	public fkClientLicense!: ClientLicenseEntity;

	// String-based reference: license_note ↔ client_license already form an
	// import cycle, and naming the class lazily keeps this file out of it.
	@ManyToOne('LicenseNoteEntity', {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_license_note__id', referencedColumnName: 'id' }])
	public fkLicenseNote!: LicenseNoteEntity | null;
}
